import type { QueryClient, QueryFilters, FetchQueryOptions } from "@tanstack/react-query";

/** A required read keeps its original query and adapter even after its observer moves or unmounts. */
export async function refreshQueryAfterWrite<T>(client:QueryClient,options:FetchQueryOptions<T>):Promise<T>{
  await client.cancelQueries({queryKey:options.queryKey,exact:true});
  return client.fetchQuery({...options,staleTime:0,retry:false});
}

/** Invalidate before reading; a cancelled pre-write fetch or an error snapshot is not an acknowledgement. */
export async function refreshQueriesAfterWrite(client: QueryClient, filters: QueryFilters = {}) {
  await client.cancelQueries(filters);
  await client.invalidateQueries({ ...filters, refetchType: "none" });
  await client.refetchQueries({ ...filters, type: "active" }, { cancelRefetch: true, throwOnError: true });
  const active = client.getQueryCache().findAll({ ...filters, type: "active" });
  await Promise.allSettled(active.flatMap(query => query.promise ? [query.promise] : []));
  for (const query of active) {
    if (query.state.status === "error") throw query.state.error;
    const requestError = (query.state.data as { requestError?: unknown } | undefined)?.requestError;
    if (requestError) throw new Error(String(requestError));
    if (query.state.fetchStatus !== "idle" || query.state.isInvalidated) throw new Error("Resource refresh incomplete");
  }
}
